import type { ReactNode } from "react";
import type { MyIssuedRosterResponse } from "@/hooks/useMyIssuedRoster";

type LegendShift = {
  readonly rowKey: string;
  readonly displayShiftId: string;
  readonly displayName: string;
  readonly color: string;
  readonly count: number;
};

export function MyRosterShiftLegend({
  wardCounts,
  sourceGroupId,
  fallback,
  wardColors,
  actions,
}: {
  readonly wardCounts: MyIssuedRosterResponse["ward_counts"];
  readonly sourceGroupId: string;
  readonly fallback: readonly LegendShift[];
  readonly wardColors: ReadonlyMap<string, string>;
  readonly actions?: ReactNode;
}) {
  const singleHome =
    wardCounts?.length === 1 && wardCounts[0].group_id === sourceGroupId;
  const groups =
    wardCounts != null
      ? wardCounts.map((ward) => ({
          key: ward.group_id,
          label: singleHome ? "" : ward.group_name || ward.group_id,
          isSource: ward.group_id === sourceGroupId,
          shifts: ward.shifts.map((shift) => ({
            rowKey: `${ward.group_id}:${shift.schedule_id ?? shift.code}`,
            displayShiftId: shift.code,
            displayName: shift.name,
            color: shift.color || "#d6d6de",
            count: shift.count,
          })),
        }))
      : [{ key: "legacy", label: "", isSource: false, shifts: fallback }];
  const visible = groups.filter((group) => group.shifts.length > 0);
  if (!visible.length && !actions) return null;
  return (
    <section
      aria-label="병동별 근무 코드"
      className="mt-6 space-y-3 rounded-xl bg-light-muted/60 p-3"
    >
      <div className="flex min-h-7 flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-secondary-foreground">
          근무 코드
        </h3>
        {actions}
      </div>
      {visible.map((group) => (
        <section key={group.key} className="min-w-0 space-y-2">
          {group.label && (
            <h4 className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs font-semibold text-muted-foreground">
              <span
                aria-hidden="true"
                className="size-2 shrink-0 rounded-full"
                style={{
                  backgroundColor: wardColors.get(group.key) || "#64748b",
                }}
              />
              <span className="min-w-0 break-words">{group.label}</span>
              {group.isSource && (
                <span className="shrink-0 rounded-sm bg-background px-1.5 py-0.5 text-[10px] font-medium">
                  본 병동
                </span>
              )}
            </h4>
          )}
          <div
            role="list"
            aria-label={
              group.label ? `${group.label} 근무 코드 범례` : "근무 코드 범례"
            }
            className="flex min-w-0 flex-wrap gap-2"
          >
            {group.shifts.map((shift) => (
              <div
                key={shift.rowKey}
                role="listitem"
                className="flex max-w-full min-w-0 items-center gap-1.5 rounded-full bg-background px-2.5 py-1.5 text-xs text-secondary-foreground shadow-xs"
              >
                <span
                  aria-hidden="true"
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: shift.color }}
                />
                <span className="shrink-0 font-semibold">
                  {shift.displayShiftId}
                </span>
                <span
                  title={shift.displayName}
                  className="min-w-0 truncate text-muted-foreground"
                >
                  {shift.displayName}
                </span>
                <span className="ml-0.5 shrink-0 border-l border-border pl-1.5 font-semibold tabular-nums">
                  {shift.count}회
                </span>
              </div>
            ))}
          </div>
        </section>
      ))}
    </section>
  );
}
